import {
  Box,
  Button,
  createStyles,
  Dialog,
  DialogTitle,
  makeStyles,
  Tab,
  Tabs,
  ThemeProvider,
  Typography,
  TextField,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Close from '@material-ui/icons/Close';
import Chat from './Chat';
import { gql, useMutation, useQuery } from '@apollo/client';
import React, { BaseSyntheticEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Patient } from '@/types/Patient';
import ThreadInfo from '../../types/ThreadInfo';
import { list as listMessage } from '../../graph/customMessages';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import { tabletThemeDark } from '../../themes/tabletTheme';
import { useScope } from '@/hooks/useScope';
import { editCaretakerNote } from '@/graph/patients';

interface ChatDialogProps {
  thread?: ThreadInfo;
  createThread: () => Promise<string>;
  markAsRead: () => Promise<void>;
  dialogTitle: string;
  dialogSubtitle: string;
  dialogAdditionalInfo?: string;
  open: boolean;
  handleClose: () => void;
  patient: Patient;
}

const ChatDialog: FC<ChatDialogProps> = ({
  thread,
  createThread,
  markAsRead,
  dialogTitle,
  dialogSubtitle,
  dialogAdditionalInfo,
  open,
  handleClose,
  patient,
}) => {
  const { data, loading, error } = useQuery(listMessage);

  // TODO: Implement proper loading state, empty state and error handling
  if (loading || error || !data) return null;

  return (
    <ChatDialogInner
      thread={thread}
      createThread={createThread}
      markAsRead={markAsRead}
      dialogTitle={dialogTitle}
      dialogSubtitle={dialogSubtitle}
      dialogAdditionalInfo={dialogAdditionalInfo}
      open={open}
      handleClose={handleClose}
      patient={patient}
      data={data}
    />
  );
};

interface ChatDialogInnerProps extends ChatDialogProps {
  data: any;
}

const ChatDialogInner: FC<ChatDialogInnerProps> = ({
  thread,
  createThread,
  markAsRead,
  dialogTitle,
  dialogSubtitle,
  dialogAdditionalInfo,
  open,
  handleClose,
  patient,
  data,
}) => {
  const scope = useScope();
  const hospitalName = scope?.hospital?.name;

  const classes = useStyles();

  const [markingAsRead, setMarkingAsRead] = useState(false);

  const markAsReadAndClose = useCallback(async () => {
    try {
      setMarkingAsRead(true);

      await markAsRead();

      handleClose?.();
    } finally {
      setMarkingAsRead(false);
    }
  }, [handleClose, markAsRead]);

  const [height, setHeight] = useState<string>('calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))');
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // Add an event listener to detect changes in window.innerHeight.
  useEffect(() => {
    const handleResize = () => {
      // Calculate the height of the visible area after the keyboard opens.
      const windowHeight = window.innerHeight;
      const keyboardHeight = 30;
      const visibleHeight = windowHeight - keyboardHeight;

      // Check if the keyboard is open based on the change in visible height.
      setIsKeyboardOpen(visibleHeight < windowHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const { visualViewport } = window;

    if (!visualViewport) {
      return;
    }

    const updateHeight = () => {
      if (isKeyboardOpen) {
        // Reduce the modal height when the keyboard is open.
        setHeight('calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - keyboardHeight)');
      } else {
        // Reset the modal height when the keyboard is closed.
        setHeight('calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom)');
      }
    };

    updateHeight();

    visualViewport.addEventListener('resize', updateHeight);

    return () => {
      visualViewport.removeEventListener('resize', updateHeight);
      setHeight('calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))');
    };
  }, [isKeyboardOpen, open]);

  const [selectedTab, setSelectedTab] = useState(-1);
  const [selectedMessageContent, setSelectedMessageContent] = useState('');

  const handleChangeTab = useCallback(
    async (event, newValue) => {
      // Update selectedMessageContent with the content of the selected message
      setSelectedTab(newValue);
      const messageContent = data.customMessages[newValue].content;
      setSelectedMessageContent(replaceChatDialogPlaceholders(messageContent, patient, hospitalName));
    },
    [data.customMessages, hospitalName, patient]
  );

  useEffect(() => {
    if (!open) {
      setSelectedTab(-1);
      setSelectedMessageContent('');
    }
  }, [open]);

  const resetSelectedTab = useCallback(() => {
    setSelectedTab(-1);
    setSelectedMessageContent('');
  }, []);

  const tabHeight = useMemo(() => {
    if (/iPhone|iPad|iPod|Android|Samsung|Tablet/.test(window.navigator.userAgent)) {
      return '600px';
    }
    return '100vh';
  }, []);

  const [isEditingNote, setIsEditingNote] = useState(false);
  const [editedNote, setEditedNote] = useState(dialogAdditionalInfo);

  const [updateCaretakerNote] = useMutation(editCaretakerNote);

  const handleEditClick = useCallback(() => {
    setIsEditingNote(true);
  }, []);

  const handleNoteChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedNote(event.target.value);
  }, []);

  const handleSaveNote = useCallback(async () => {
    try {
      await updateCaretakerNote({ variables: { id: patient.id, note: editedNote } });
      setIsEditingNote(false);
    } catch (error) {
      console.error('Error updating caretaker note:', error);
    }
  }, [editedNote, patient.id, updateCaretakerNote]);

  const handleCancelEdit = useCallback(() => {
    setIsEditingNote(false);
    setEditedNote(dialogAdditionalInfo);
  }, [dialogAdditionalInfo]);

  return (
    <ThemeProvider theme={tabletThemeDark}>
      <Box onClick={stopChatDialogEventPropagation}>
        <Dialog open={open} onClose={handleClose} maxWidth="md">
          <DialogTitle id="form-dialog-title">
            <Box display="flex" justifyContent="space-between">
              <div>
                <Typography variant="h4">{dialogTitle}</Typography>
                <Typography color="textSecondary" style={{ fontSize: '1.25rem' }}>
                  {dialogSubtitle}
                </Typography>
                {isEditingNote ? (
                  <Box display="flex" alignItems="center">
                    <TextField
                      value={editedNote}
                      onChange={handleNoteChange}
                      variant="outlined"
                      size="small"
                      fullWidth
                      autoFocus
                    />
                    <Button variant="contained" color="primary" onClick={handleSaveNote}>
                      Save
                    </Button>
                    <Button variant="text" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Typography color="textSecondary" onClick={handleEditClick} style={{ cursor: 'pointer' }}>
                    {dialogAdditionalInfo ? `Notes: ${dialogAdditionalInfo}` : ''}
                  </Typography>
                )}
              </div>
              {!!handleClose && (
                <div>
                  {thread?.hasUnreadMessages && (
                    <Button aria-label="close" onClick={markAsReadAndClose} disabled={markingAsRead}>
                      {markingAsRead ? 'Marking as Read…' : 'Mark as Read and Close'}
                    </Button>
                  )}
                  <IconButton aria-label="close" onClick={handleClose}>
                    <Close />
                  </IconButton>
                </div>
              )}
            </Box>
          </DialogTitle>
          <DialogContent className={classes.content} dividers>
            <Box>
              <Typography variant="h5" gutterBottom>
                Templates
              </Typography>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={selectedTab}
                onChange={handleChangeTab}
                aria-label="Chat Tabs"
                indicatorColor="primary"
              >
                {!!data.customMessages &&
                  Array.isArray(data.customMessages) &&
                  data.customMessages.map(item => <Tab key={item.id} label={item.title} />)}
              </Tabs>
            </Box>
            <div>
              <Divider orientation="vertical" />
            </div>
            <div className={classes.chatArea}>
              <Chat
                threadId={thread?.id}
                createThread={createThread}
                messageContent={selectedMessageContent}
                resetSelectedTab={resetSelectedTab}
              />
            </div>
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

function useMarkAsRead(threadId?: string) {
  const [markMessagesAsRead] = useMutation(gql`
    mutation markMessagesAsRead($threadId: String!) {
      markMessagesAsRead(threadId: $threadId)
    }
  `);

  return async () => {
    if (!threadId) {
      return;
    }

    await markMessagesAsRead({ variables: { threadId } });
  };
}

interface CaretakerChatDialogProps {
  patient?: Patient;
  open: boolean;
  handleClose: () => void;
}

export const CaretakerChatDialog: FC<CaretakerChatDialogProps> = ({ patient, open, handleClose }) => {
  const [createCaretakerThread] = useMutation(gql`
    mutation createCaretakerThread($patientId: Long!) {
      createCaretakerThread(patientId: $patientId)
    }
  `);

  const patientId = patient?.id;

  const createThread = useCallback(async () => {
    if (!patientId) {
      return null;
    }
    const result = await createCaretakerThread({ variables: { patientId } });

    return result.data.createCaretakerThread;
  }, [createCaretakerThread, patientId]);

  const markAsRead = useMarkAsRead(patient?.caretakerThread?.id);

  if (!patient) {
    return null;
  }

  return (
    <ChatDialog
      thread={patient?.caretakerThread}
      markAsRead={markAsRead}
      createThread={createThread}
      dialogTitle={`Send Message to Caretaker`}
      dialogSubtitle={`Patient ${patient?.name} • Caretaker Phone Number ${patient?.notificationNumbers}${
        patient?.caretakerName ? ` • Caretaker Name ${patient.caretakerName}` : ''
      }`}
      dialogAdditionalInfo={patient?.caretakerNote}
      open={open}
      handleClose={handleClose}
      patient={patient}
    />
  );
};

interface PatientChatDialogProps {
  patient?: Patient;
  open: boolean;
  handleClose: () => void;
}

export const PatientChatDialog: FC<PatientChatDialogProps> = ({ patient, open, handleClose }) => {
  const [createPatientThread] = useMutation(gql`
    mutation createPatientThread($patientId: Long!) {
      createPatientThread(patientId: $patientId)
    }
  `);

  const patientId = patient?.id;

  const createThread = useCallback(async () => {
    if (!patientId) {
      return null;
    }
    const result = await createPatientThread({ variables: { patientId } });

    return result.data.createPatientThread;
  }, [createPatientThread, patientId]);

  const markAsRead = useMarkAsRead(patient?.patientThread?.id);

  if (!patientId) {
    return null;
  }

  return (
    <ChatDialog
      thread={patient?.patientThread}
      markAsRead={markAsRead}
      createThread={createThread}
      dialogTitle={`Send Message to Patient`}
      dialogSubtitle={`Patient ${patient?.name} • Patient Phone Number ${patient?.phoneNumber}`}
      open={open}
      handleClose={handleClose}
      patient={patient}
    />
  );
};

interface PreOpPatientChatDialogProps {
  patient?: Patient;
  open: boolean;
  handleClose: () => void;
}

export const PreOpPatientChatDialog: FC<PreOpPatientChatDialogProps> = ({ patient, open, handleClose }) => {
  const [createPatientThreadForProcedure] = useMutation(gql`
    mutation createPatientThreadForProcedure($procedureId: Long!) {
      createPatientThreadForProcedure(procedureId: $procedureId)
    }
  `);

  const procedureId = patient?.procedureId;

  const createThread = useCallback(async () => {
    if (!procedureId) {
      return null;
    }
    const result = await createPatientThreadForProcedure({ variables: { procedureId } });

    return result.data.createPatientThreadForProcedure;
  }, [createPatientThreadForProcedure, procedureId]);

  const markAsRead = useMarkAsRead(patient?.patientThread?.id);

  if (!patient) {
    return null;
  }

  return (
    <ChatDialog
      thread={patient?.patientThread}
      markAsRead={markAsRead}
      createThread={createThread}
      dialogTitle={`Send Message to Patient`}
      dialogSubtitle={`Patient ${patient?.name} • Patient Phone Number ${patient?.phoneNumber}`}
      open={open}
      handleClose={handleClose}
      patient={patient}
    />
  );
};

const useStyles = makeStyles(theme =>
  createStyles({
    content: {
      display: 'flex',
      gap: theme.spacing(2),
      minHeight: 400,
    },
    chatArea: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
    },
  })
);

const stopChatDialogEventPropagation = (e: BaseSyntheticEvent<unknown, unknown>) => {
  e.stopPropagation();
  e.preventDefault();
};

const replaceChatDialogPlaceholders = (content: string, patient: Patient, hospitalName: string) => {
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString();
  const formattedTime = currentDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

  let physicianName = '';
  if (typeof patient?.physician === 'string') {
    physicianName = patient?.physician;
  } else if (typeof patient?.physician === 'object') {
    physicianName = patient?.physician?.name || '';
  }

  return content
    .replace(/{{patientFullName}}/g, patient?.name || '')
    .replace(/{{physicianName}}/g, physicianName)
    .replace(/{{patientInitials}}/g, patient?.initials || '')
    .replace(/{{hospitalName}}/g, hospitalName)
    .replace(/{{date}}/g, formattedDate)
    .replace(/{{currentTime}}/g, formattedTime);
};
